import React, { useEffect, useState } from "react";
import {
    Grid, Typography, Box, Card, CardContent, Chip, Avatar,
    List, ListItem, ListItemText, ListItemAvatar, CircularProgress,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper
} from "@mui/material";
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell,
    ResponsiveContainer, Legend, CartesianGrid, LineChart, Line, AreaChart, Area
} from "recharts";
import {
    People, Assignment, Event, TrendingUp,
    Schedule, EmojiEvents, Notifications, Warning
} from "@mui/icons-material";
import axios from "axios";
import { BASE_URL } from "app/services/auth-services";
import { todayDate } from "app/shared/ReuseComponents/DateFormatter";

const COLORS = ["#8884d8", "#82ca9d", "#ffc658", "#ff8042", "#0088FE", "#00C49F"];

// Sample data for fallback
const sampleData = {
    departmentStats: {
        totalEmployees: 24,
        presentToday: 18,
        onLeave: 4,
        remoteWork: 2
    },
    attendanceTrend: [
        { date: '01 Sep', present: 20, absent: 4 },
        { date: '02 Sep', present: 22, absent: 2 },
        { date: '03 Sep', present: 19, absent: 5 },
        { date: '04 Sep', present: 23, absent: 1 },
        { date: '05 Sep', present: 21, absent: 3 },
        { date: '06 Sep', present: 24, absent: 0 },
        { date: '07 Sep', present: 18, absent: 6 },
    ],
    leaveDistribution: [
        { name: 'Sick Leave', value: 12 },
        { name: 'Vacation', value: 8 },
        { name: 'Personal', value: 5 },
        { name: 'Other', value: 3 },
    ],
    teamPerformance: [
        { name: 'Team A', value: 85 },
        { name: 'Team B', value: 92 },
        { name: 'Team C', value: 78 },
        { name: 'Team D', value: 88 },
    ],
    pendingApprovals: {
        leaveRequests: 5,
        timesheets: 8,
        expenses: 3
    },
    upcomingEvents: [
        { id: 1, title: 'Quarterly Review', date: '2023-09-20', type: 'Meeting' },
        { id: 2, title: 'Project Deadline', date: '2023-09-25', type: 'Deadline' },
        { id: 3, title: 'Team Building', date: '2023-09-30', type: 'Event' },
    ],
    recentActivities: [
        { id: 1, employee: 'John Doe', action: 'Submitted timesheet', time: '2 hours ago' },
        { id: 2, employee: 'Jane Smith', action: 'Requested leave', time: '5 hours ago' },
        { id: 3, employee: 'Robert Johnson', action: 'Completed training', time: 'Yesterday' },
        { id: 4, employee: 'Sarah Williams', action: 'Updated project status', time: 'Yesterday' },
    ]
};

const StatCard = ({ title, value, icon, color }) => (
    <Card sx={{ height: '100%', borderRadius: 3, boxShadow: 3 }}>
        <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                    <Typography color="textSecondary" gutterBottom variant="h6">
                        {title}
                    </Typography>
                    <Typography variant="h4" component="div">
                        {value}
                    </Typography>
                </Box>
                <Avatar sx={{ bgcolor: color, width: 56, height: 56 }}>
                    {icon}
                </Avatar>
            </Box>
        </CardContent>
    </Card>
);

const DepartmentManagerDashboard = () => {
    const token = localStorage.getItem("accesstoken");
    const userId = parseInt(localStorage.getItem("UserId"));
    const partnerId = parseInt(localStorage.getItem("PartnerId"));
    const companyId = parseInt(localStorage.getItem("OrganizationId"));
    const departmentId = parseInt(localStorage.getItem("DepartmentId"));

    const header = {
        headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
        },
    };

    let userDetails = localStorage.getItem("UserDetails");
    userDetails = JSON.parse(userDetails);

    const [data, setData] = useState(sampleData);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                setLoading(true);
                setError(null);

                const employeeResponse = await axios.get(
                    `${BASE_URL}/Felix/getemployeeeid/?email=${userDetails?.email}`,
                    header
                );
                const employeeStats = employeeResponse?.data;
                const department = employeeStats?.[0]?.Department;

                // In a real application, these would be actual API endpoints for manager data
                const [departmentStats, attendanceData, leaveData] = await Promise.allSettled([
                    axios.get(`${BASE_URL}/Felix/HRDashboarddepartmentwiseemployee/?Company_Id=${companyId}&Partner_Id=${partnerId}&Department=${department}`),
                    axios.get(`${BASE_URL}/manager/attendance-trend/?department_id=${departmentId}`, header),
                    axios.get(`${BASE_URL}/manager/leave-distribution/?department_id=${departmentId}`, header),
                ]);

                // Process API responses
                const departmentStatsArray = Object.entries(departmentStats.value.data).map(([key, value]) => ({
                    name: key,
                    totalEmployees: value
                }));

                setData({
                    departmentStats: departmentStatsArray.find(dep => dep.name === department),
                    attendanceTrend: attendanceData.value.data,
                    leaveDistribution: leaveData.value.data,
                    // In a real app, you would fetch other data points too
                    pendingApprovals: sampleData.pendingApprovals,
                    recentActivities: sampleData.recentActivities
                });

            } catch (err) {
                console.error("API request failed. Using sample data as fallback.", err);
                // setError("Live data unavailable. Showing sample data.");
                // setData(sampleData);
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, [departmentId]);


    console.log("Data", data)

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <>
            <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
                Department Manager Dashboard
            </Typography>

            {error && (
                <Typography variant="body2" color="warning.main" mb={2}>
                    ⚠️ {error}
                </Typography>
            )}

            {/* Summary Stats Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        title="Total Employees"
                        value={data?.departmentStats?.totalEmployees}
                        icon={<People />}
                        color="#3f51b5"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        title="Present Today"
                        value={data?.departmentStats?.presentToday}
                        icon={<Event />}
                        color="#4caf50"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        title="On Leave"
                        value={data?.departmentStats?.onLeave}
                        icon={<Schedule />}
                        color="#ff9800"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        title="Remote Work"
                        value={data?.departmentStats?.remoteWork}
                        icon={<TrendingUp />}
                        color="#9c27b0"
                    />
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                {/* Attendance Trend Chart */}
                <Grid item xs={12} md={8}>
                    <Card sx={{ p: 2, borderRadius: 3, boxShadow: 3, height: '100%' }}>
                        <Typography variant="h6" gutterBottom>
                            Attendance Trend (Last 7 Days)
                        </Typography>
                        <ResponsiveContainer width="100%" height={300}>
                            <AreaChart data={data?.attendanceTrend}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="date" />
                                <YAxis />
                                <Tooltip />
                                <Area type="monotone" dataKey="present" stackId="1" stroke="#82ca9d" fill="#82ca9d" />
                                <Area type="monotone" dataKey="absent" stackId="2" stroke="#ff8042" fill="#ff8042" />
                                <Legend />
                            </AreaChart>
                        </ResponsiveContainer>
                    </Card>
                </Grid>

                {/* Leave Distribution Pie Chart */}
                <Grid item xs={12} md={4}>
                    <Card sx={{ p: 2, borderRadius: 3, boxShadow: 3, height: '100%' }}>
                        <Typography variant="h6" gutterBottom>
                            Leave Distribution
                        </Typography>
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie
                                    data={data?.leaveDistribution}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={100}
                                    label
                                >
                                    {data?.leaveDistribution?.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </Card>
                </Grid>

                {/* Pending Approvals */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ p: 2, borderRadius: 3, boxShadow: 3, height: '100%' }}>
                        <Typography variant="h6" gutterBottom>
                            Pending Approvals
                        </Typography>
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid item xs={12} sm={4}>
                                <Box textAlign="center">
                                    <Avatar sx={{ bgcolor: 'warning.main', width: 56, height: 56, mx: 'auto' }}>
                                        <Assignment />
                                    </Avatar>
                                    <Typography variant="h4" sx={{ mt: 1 }}>
                                        {data?.pendingApprovals?.timesheets}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Timesheets
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Box textAlign="center">
                                    <Avatar sx={{ bgcolor: 'info.main', width: 56, height: 56, mx: 'auto' }}>
                                        <Event />
                                    </Avatar>
                                    <Typography variant="h4" sx={{ mt: 1 }}>
                                        {data?.pendingApprovals?.leaveRequests}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Leave Requests
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Box textAlign="center">
                                    <Avatar sx={{ bgcolor: 'error.main', width: 56, height: 56, mx: 'auto' }}>
                                        <Notifications />
                                    </Avatar>
                                    <Typography variant="h4" sx={{ mt: 1 }}>
                                        {data?.pendingApprovals?.expenses}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Expenses
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Card>
                </Grid>

                {/* Recent Activities */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ p: 2, borderRadius: 3, boxShadow: 3, height: '100%' }}>
                        <Typography variant="h6" gutterBottom>
                            Recent Activities
                        </Typography>
                        <List>
                            {data?.recentActivities?.map((activity) => (
                                <ListItem key={activity.id} divider>
                                    <ListItemAvatar>
                                        <Avatar>
                                            {activity.employee.charAt(0)}
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={activity.action}
                                        secondary={`${activity.employee} - ${activity.time}`}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Card>
                </Grid>
            </Grid>
        </>
    );
};

export default DepartmentManagerDashboard;